import React, { useEffect, useState } from 'react'
import { UsersTablePageProps, usersTableWidths } from './UsersTablePage.types'
import styles from './UsersTablePage.module.scss'
import cl from 'classnames'
import { Button, ImagePortrait, PageHeader, TableBodyContainer, TableBodyLine, TableCellWithAdditionalCount, TableContainer, TableHeader, Text1 } from 'components/shared'
import { IconMinusCircle, IconPlusCircle, IconUserManagement } from 'assets'
import { inject } from 'mobx-react'
import {
  USERS_TABLE_STORE,
  UsersTableSortProps
} from 'store'
import { observer } from 'mobx-react-lite'
import { noImageAvatar, User } from 'utilities'
import { AvatarImage } from 'components'
import { UserActionHistoryTable } from '../UserActionHistoryTable'
import tableBodyContainerStyle from '../../../components/shared/TableBodyContainer/TableBodyContainer.module.scss'

const UsersTablePageComponent = (props: UsersTablePageProps) => {
  const {
    [USERS_TABLE_STORE]: {
      getUsers,
      searchProps,
      onSearchPropsChange,
      getSortDirection,
      handleSortDirectionChange,
      isColumnMarked,
      userRoleSelectOptions,
      searchResults,
      getProjectsInfo
    },
    history: {
      push
    }
  } = props

  useEffect(getUsers, [getUsers])

  const [expandedUserId, setExpandedUserId] = useState<number>(null)
  const scrollbarSpacing = 4.8

  return (
    <div className={cl(styles.pageContainer)} style={{ overflow: 'hidden' }}>
      <PageHeader
        navSegments={[{
          label: 'User management',
          icon: IconUserManagement,
          iconProps: { viewBox: '0 0 22 22' }
        }]}
        buttons={[{
          preset: 'primary',
          children: 'Invite user',
          onClick: () => push('/user-management/invite')
        }]}
      />
      <TableContainer className={styles.thinScroll} style={ { overflowX: 'auto' } }>
        <TableHeader
          tableColumns={[
            {
              label: 'User',
              // widthPercentage: usersTableWidths.name,
              widthPx: usersTableWidths.name,
              className: styles.firstHeaderCol,
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.name,
                onChange: name => onSearchPropsChange({ name })
              },
              sortDirection: getSortDirection(UsersTableSortProps.NAME),
              setSortDirection: handleSortDirectionChange(UsersTableSortProps.NAME),
              isColumnMarked: isColumnMarked(UsersTableSortProps.NAME)
            },
            {
              label: 'User role',
              // widthPercentage: usersTableWidths.role,
              widthPx: usersTableWidths.role,
              selectProps: {
                options: userRoleSelectOptions,
                value: searchProps.role || '',
                onChange: role => onSearchPropsChange({ role }),
                noneLabel: 'All'
              },
              sortDirection: getSortDirection(UsersTableSortProps.ROLE),
              setSortDirection: handleSortDirectionChange(UsersTableSortProps.ROLE),
              isColumnMarked: isColumnMarked(UsersTableSortProps.ROLE)
            },
            {
              label: 'Projects',
              // widthPercentage: usersTableWidths.projects,
              widthPx: usersTableWidths.projects,
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.projects,
                onChange: projects => onSearchPropsChange({ projects })
              }
            },
            {
              label: 'Added on',
              // widthPercentage: usersTableWidths.addedOn,
              widthPx: usersTableWidths.addedOn,
              periodFilterProps: {
                period: searchProps.addedOnPeriod,
                setPeriod: addedOnPeriod => onSearchPropsChange({ addedOnPeriod })
              },
              sortDirection: getSortDirection(UsersTableSortProps.ADDED_ON),
              setSortDirection: handleSortDirectionChange(UsersTableSortProps.ADDED_ON),
              isColumnMarked: isColumnMarked(UsersTableSortProps.ADDED_ON)
            },
            {
              label: 'Added by',
              // widthPercentage: usersTableWidths.addedBy,
              widthPx: usersTableWidths.addedBy + scrollbarSpacing,
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.addedBy,
                onChange: addedBy => onSearchPropsChange({ addedBy })
              },
              sortDirection: getSortDirection(UsersTableSortProps.ADDED_BY),
              setSortDirection: handleSortDirectionChange(UsersTableSortProps.ADDED_BY),
              isColumnMarked: isColumnMarked(UsersTableSortProps.ADDED_BY)
            }
          ]}
        />
        <TableBodyContainer className={tableBodyContainerStyle.tableBodyContainer}>
          {searchResults.map(user => {
            const isExpanded = expandedUserId === user.id
            return (
              <TableBodyLine
                key={user.id}
                cells={[
                  {
                    // widthPercentage: usersTableWidths.name,
                    widthPx: usersTableWidths.name,
                    isColumnMarked: isColumnMarked(UsersTableSortProps.NAME),
                    className: styles.firstBodyCol,
                    children: (
                      <>
                        <Button
                          preset='icon'
                          onClick={() => setExpandedUserId(isExpanded ? null : user.id)}
                        >
                          {isExpanded
                            ? <IconMinusCircle />
                            : <IconPlusCircle />
                          }
                        </Button>
                        <Button
                          preset='icon'
                          onClick={() => push(
                            User.getId() === user.id
                              ? '/user-management/my-profile'
                              : `/user-management/${user.id}`
                          )}
                        >
                          <ImagePortrait
                            style={{ marginLeft: 3 }}
                            width={23.8}
                            src={user.imageUrl || noImageAvatar}
                          />
                          <Text1
                            bold
                            overflowEllipsis
                            style={{ marginLeft: 20 }}
                          >
                            {user.name}
                          </Text1>
                        </Button>
                      </>
                    )
                  },
                  {
                    // widthPercentage: usersTableWidths.role,
                    widthPx: usersTableWidths.role,
                    isColumnMarked: isColumnMarked(UsersTableSortProps.ROLE),
                    children: (
                      <Text1
                        overflowEllipsis
                        title={user.rolesAsString}
                      >
                        {user.rolesAsString}
                      </Text1>
                    )
                  },
                  {
                    // widthPercentage: usersTableWidths.projects,
                    widthPx: usersTableWidths.projects,
                    children: (
                      <TableCellWithAdditionalCount
                        {...getProjectsInfo(user.id)}
                      />
                    )
                  },
                  {
                    // widthPercentage: usersTableWidths.addedOn,
                    widthPx: usersTableWidths.addedOn,
                    isColumnMarked: isColumnMarked(UsersTableSortProps.ADDED_ON),
                    children: <Text1 overflowEllipsis>{user.created.format('DD.MM.YYYY')}</Text1>
                  },
                  {
                    // widthPercentage: usersTableWidths.addedBy,
                    widthPx: usersTableWidths.addedBy,
                    isColumnMarked: isColumnMarked(UsersTableSortProps.ADDED_BY),
                    children: user.createdBy
                      ? (
                        <>
                          <AvatarImage
                            src={user.createdBy.imageUrl}
                            size={34}
                            className={styles.noBoxShadow}
                          />
                          <Text1 overflowEllipsis style={{ margin: '0 5px 0 11px' }}>
                            {user.createdBy.name}
                          </Text1>
                          {Boolean(user.createdBy.highestPriorityRoleLabel) && (
                            <>
                              (
                              <Text1 overflowEllipsis>
                                {user.createdBy.highestPriorityRoleLabel}
                              </Text1>
                              )
                            </>
                          )}
                        </>
                      )
                      : null
                  }
                ]}
                isExpanded={isExpanded}
                expandComponent={<UserActionHistoryTable userId={user.id} />}
              />
            )
          })}
        </TableBodyContainer>
      </TableContainer>
    </div>
  )
}

const UsersTablePage = inject(
  USERS_TABLE_STORE
)(observer(UsersTablePageComponent))
export { UsersTablePage }
